import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Combobox } from '@/components/ui/combobox';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { ClientPaginationWrapper } from '@/components/ui/ClientPaginationWrapper';
import { toast } from 'sonner';
import axios from 'axios';
import { API_URL } from '../../lib/api-client';
import { 
  Upload, Download, Check, X, Loader2, Users, FileJson,
  Image, User, Calendar, MapPin, Briefcase, Heart, Eye, Trash2,
  RefreshCw, Edit2, Save, EyeOff, CheckSquare, Square
} from 'lucide-react';
import { RELIGION_OPTIONS, MOTHER_TONGUE_OPTIONS } from '../../lib/constants';
import { countries, states, cities } from '../../utils/locationData';

// Enum options
const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' }
];

const MARITAL_STATUS_OPTIONS = [
  { value: 'never_married', label: 'Never Married' },
  { value: 'divorced', label: 'Divorced' },
  { value: 'widowed', label: 'Widowed' },
  { value: 'separated', label: 'Separated' }
];

const EDUCATION_LEVEL_OPTIONS = [
  { value: 'high_school', label: 'High School' },
  { value: 'diploma', label: 'Diploma' },
  { value: 'bachelors', label: 'Bachelors' },
  { value: 'masters', label: 'Masters' },
  { value: 'phd', label: 'PhD' },
  { value: 'other', label: 'Other' }
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'government', label: 'Government' },
  { value: 'private', label: 'Private' },
  { value: 'business', label: 'Business' },
  { value: 'self_employed', label: 'Self Employed' },
  { value: 'not_working', label: 'Not Working' }
];

const ANNUAL_INCOME_OPTIONS = [
  { value: '0-3', label: '0-3 Lakhs' },
  { value: '3-5', label: '3-5 Lakhs' },
  { value: '5-7', label: '5-7 Lakhs' },
  { value: '7-10', label: '7-10 Lakhs' },
  { value: '10-15', label: '10-15 Lakhs' },
  { value: '15-20', label: '15-20 Lakhs' },
  { value: '20-30', label: '20-30 Lakhs' },
  { value: '30-50', label: '30-50 Lakhs' },
  { value: '50+', label: '50+ Lakhs' }
];

const DIET_OPTIONS = [
  { value: 'vegetarian', label: 'Vegetarian' },
  { value: 'non_vegetarian', label: 'Non-Vegetarian' },
  { value: 'eggetarian', label: 'Eggetarian' },
  { value: 'vegan', label: 'Vegan' }
];

const HABIT_OPTIONS = [
  { value: 'no', label: 'No' },
  { value: 'occasionally', label: 'Occasionally' },
  { value: 'yes', label: 'Yes' }
];

// Height options (in cm)
const HEIGHT_OPTIONS = [
  { value: 137, label: '4\' 6" (137 cm)' },
  { value: 140, label: '4\' 7" (140 cm)' },
  { value: 142, label: '4\' 8" (142 cm)' },
  { value: 145, label: '4\' 9" (145 cm)' },
  { value: 147, label: '4\' 10" (147 cm)' },
  { value: 150, label: '4\' 11" (150 cm)' },
  { value: 152, label: '5\' 0" (152 cm)' },
  { value: 155, label: '5\' 1" (155 cm)' },
  { value: 157, label: '5\' 2" (157 cm)' },
  { value: 160, label: '5\' 3" (160 cm)' },
  { value: 163, label: '5\' 4" (163 cm)' },
  { value: 165, label: '5\' 5" (165 cm)' },
  { value: 168, label: '5\' 6" (168 cm)' },
  { value: 170, label: '5\' 7" (170 cm)' },
  { value: 173, label: '5\' 8" (173 cm)' },
  { value: 175, label: '5\' 9" (175 cm)' },
  { value: 178, label: '5\' 10" (178 cm)' },
  { value: 180, label: '5\' 11" (180 cm)' },
  { value: 183, label: '6\' 0" (183 cm)' },
  { value: 185, label: '6\' 1" (185 cm)' },
  { value: 188, label: '6\' 2" (188 cm)' },
  { value: 191, label: '6\' 3" (191 cm)' },
  { value: 193, label: '6\' 4" (193 cm)' },
  { value: 196, label: '6\' 5" (196 cm)' },
  { value: 198, label: '6\' 6" (198 cm)' },
  { value: 201, label: '6\' 7" (201 cm)' },
  { value: 203, label: '6\' 8" (203 cm)' },
];

interface ParsedProfile {
  name: string;
  email: string;
  phone: string;
  gender: string;
  dateOfBirth: string;
  age: number;
  height: number; // Changed to number (cm)
  maritalStatus: string;
  religion: string;
  community: string;
  motherTongue: string;
  city: string;
  state: string;
  country: string;
  education: string;
  educationField: string; // Added education field
  profession: string;
  company: string; // Added company field
  workingWith: string;
  annualIncome: string;
  familyIncome: string; // Added family income
  diet: string;
  drink: string;
  smoke: string;
  about: string;
  familyDetails: string;
  fatherProfession: string;
  motherProfession: string;
  siblings: string;
  interests: string[];
  images: string[];
  rawData?: any;
  partnerPreferences?: any; // Added partner preferences
}

export default function ProfileImporter() {
  const [jsonInput, setJsonInput] = useState('');
  const [editableProfile, setEditableProfile] = useState<ParsedProfile | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [importedProfiles, setImportedProfiles] = useState<any[]>([]);
  const [loadingProfiles, setLoadingProfiles] = useState(false);
  const [selectedProfiles, setSelectedProfiles] = useState<Set<string>>(new Set());
  const [updatingStatus, setUpdatingStatus] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  // Fetch imported profiles on mount
  useEffect(() => {
    fetchImportedProfiles();
  }, []);

  const fetchImportedProfiles = async () => {
    try {
      setLoadingProfiles(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/admin/imported-profiles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        console.log('📥 Fetched imported profiles:', response.data.data);
        setImportedProfiles(response.data.data);
      }
    } catch (error: any) {
      console.error('❌ Failed to fetch imported profiles:', error);
      toast.error(error.response?.data?.message || 'Failed to fetch profiles');
    } finally {
      setLoadingProfiles(false);
    }
  };

  const handleDeleteProfile = async (profileId: string) => {
    if (!confirm('Are you sure you want to delete this profile?')) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(`${API_URL}/admin/imported-profiles/${profileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        toast.success('Profile deleted');
        fetchImportedProfiles();
      }
    } catch (error: any) {
      toast.error('Failed to delete profile');
    }
  };

  const handleToggleProfileStatus = async (profileId: string, currentStatus: boolean) => {
    try {
      setUpdatingStatus(profileId);
      const token = localStorage.getItem('token');
      const response = await axios.patch(
        `${API_URL}/admin/imported-profiles/${profileId}/toggle-status`,
        { isActive: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        const newStatus = response.data.data.isActive;
        toast.success(newStatus ? 'Profile activated - now visible in discovery' : 'Profile deactivated - hidden from discovery');
        
        // Update local state immediately for better UX
        setImportedProfiles(prev => 
          prev.map(p => 
            p._id === profileId ? { ...p, isActive: newStatus } : p
          )
        );
      }
    } catch (error: any) {
      toast.error('Failed to update profile status');
    } finally {
      setUpdatingStatus(null);
    }
  };

  const toggleProfileSelection = (profileId: string) => {
    const newSelected = new Set(selectedProfiles);
    if (newSelected.has(profileId)) {
      newSelected.delete(profileId);
    } else {
      newSelected.add(profileId);
    }
    setSelectedProfiles(newSelected);
  };

  const toggleSelectAll = () => {
    if (selectedProfiles.size === importedProfiles.length) {
      setSelectedProfiles(new Set());
    } else {
      setSelectedProfiles(new Set(importedProfiles.map(p => p._id)));
    }
  };

  const handleBulkActivate = async () => {
    if (selectedProfiles.size === 0) {
      toast.error('No profiles selected');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        Array.from(selectedProfiles).map(profileId =>
          axios.patch(
            `${API_URL}/admin/imported-profiles/${profileId}/toggle-status`,
            { isActive: true },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      
      toast.success(`${selectedProfiles.size} profiles activated`);
      setSelectedProfiles(new Set());
      fetchImportedProfiles();
    } catch (error) {
      toast.error('Failed to activate profiles');
    }
  };

  const handleBulkDeactivate = async () => {
    if (selectedProfiles.size === 0) {
      toast.error('No profiles selected');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await Promise.all(
        Array.from(selectedProfiles).map(profileId =>
          axios.patch(
            `${API_URL}/admin/imported-profiles/${profileId}/toggle-status`,
            { isActive: false },
            { headers: { Authorization: `Bearer ${token}` } }
          )
        )
      );
      
      toast.success(`${selectedProfiles.size} profiles deactivated`);
      setSelectedProfiles(new Set());
      fetchImportedProfiles();
    } catch (error) {
      toast.error('Failed to deactivate profiles');
    }
  };

  // Normalization helpers
  const normalizeGender = (gender: string): string => {
    if (!gender) return 'male';
    const n = gender.toLowerCase();
    if (n.includes('female') || n.includes('woman')) return 'female';
    if (n.includes('male') || n.includes('man')) return 'male';
    return 'male';
  };

  const normalizeMaritalStatus = (status: string): string => {
    if (!status) return 'never_married';
    const n = status.toLowerCase().replace(/\s+/g, '_');
    if (n.includes('never') || n.includes('unmarried')) return 'never_married';
    if (n.includes('divorce')) return 'divorced';
    if (n.includes('widow')) return 'widowed';
    if (n.includes('separat')) return 'separated';
    return 'never_married';
  };

  const normalizeEducation = (education: string): string => {
    if (!education) return 'bachelors';
    const n = education.toLowerCase();
    if (n.includes('phd') || n.includes('doctorate')) return 'phd';
    if (n.includes('master') || n.includes('mba') || n.includes('m.tech') || n.includes('m.e')) return 'masters';
    if (n.includes('bachelor') || n.includes('b.tech') || n.includes('b.e') || n.includes('b.e /') || n.includes('engineering')) return 'bachelors';
    if (n.includes('diploma')) return 'diploma';
    if (n.includes('high school') || n.includes('12th')) return 'high_school';
    return 'bachelors';
  };

  const normalizeEmployment = (type: string): string => {
    if (!type) return 'private';
    const n = type.toLowerCase();
    if (n.includes('government') || n.includes('public sector')) return 'government';
    if (n.includes('private')) return 'private';
    if (n.includes('business') || n.includes('own')) return 'business';
    if (n.includes('self')) return 'self_employed';
    if (n.includes('not working')) return 'not_working';
    return 'private';
  };

  const normalizeIncome = (income: string): string => {
    if (!income) return '5-7';
    const match = income.match(/(\d+)/);
    if (!match) return '5-7';
    const v = parseInt(match[1]);
    if (v < 3) return '0-3';
    if (v < 5) return '3-5';
    if (v < 7) return '5-7';
    if (v < 10) return '7-10';
    if (v < 15) return '10-15';
    if (v < 20) return '15-20';
    if (v < 30) return '20-30';
    if (v < 50) return '30-50';
    return '50+';
  };

  const normalizeReligion = (religion: string): string => {
    if (!religion) return 'hindu';
    const n = religion.toLowerCase();
    if (n.includes('hindu')) return 'hindu';
    if (n.includes('muslim') || n.includes('islam')) return 'muslim';
    if (n.includes('christian')) return 'christian';
    if (n.includes('sikh')) return 'sikh';
    if (n.includes('buddhist')) return 'buddhist';
    if (n.includes('jain')) return 'jain';
    if (n.includes('parsi')) return 'parsi';
    if (n.includes('jewish')) return 'jewish';
    return 'hindu';
  };

  const normalizeDiet = (diet: string): string => {
    if (!diet) return 'vegetarian';
    const n = diet.toLowerCase();
    if (n.includes('veg') && !n.includes('non')) return 'vegetarian';
    if (n.includes('non') || n.includes('meat')) return 'non_vegetarian';
    if (n.includes('egg')) return 'eggetarian';
    if (n.includes('vegan')) return 'vegan';
    return 'vegetarian';
  };

  const normalizeHabit = (habit: string): string => {
    if (!habit) return 'no';
    const n = habit.toLowerCase();
    if (n.includes('no') || n.includes('never')) return 'no';
    if (n.includes('occasion')) return 'occasionally';
    if (n.includes('yes') || n.includes('regular')) return 'yes';
    return 'no';
  };

  const parseProfileData = () => {
    try {
      setIsProcessing(true);
      const data = JSON.parse(jsonInput);
      const profileData = data.data || data;
      
      // Parse images from photos object - prioritize this
      const images: string[] = [];
      if (profileData.photos?.photos) {
        profileData.photos.photos.forEach((photo: any) => {
          if (photo.domain_name) {
            // Add all available sizes
            if (photo['750X1004']) images.push(photo.domain_name + photo['750X1004']);
            if (photo['750X1333']) images.push(photo.domain_name + photo['750X1333']);
            if (photo['450X600']) images.push(photo.domain_name + photo['450X600']);
            if (photo['400X500']) images.push(photo.domain_name + photo['400X500']);
            if (photo.large) images.push(photo.domain_name + photo.large);
          }
        });
      }
      
      // Fallback to direct photo fields
      if (profileData.enhancedFullPhoto) images.push(profileData.enhancedFullPhoto);
      if (profileData.largePhoto) images.push(profileData.largePhoto);
      if (profileData.fullPhoto) images.push(profileData.fullPhoto);
      if (profileData.photo) images.push(profileData.photo);
      
      const uniqueImages = [...new Set(images.filter(url => 
        url && typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))
      ))];

      // Extract age and height
      const ageHeightInfo = profileData.base?.infoMap?.find((item: any) => 
        item.key === 'info-0' || item.label === 'Age / Height');
      let age = 25;
      let heightInCm = 160; // Default height in cm
      if (ageHeightInfo) {
        const ageMatch = ageHeightInfo.value.match(/(\d+)/);
        if (ageMatch) age = parseInt(ageMatch[1]);
        
        // Parse height: "5' 7""
        const heightMatch = ageHeightInfo.value.match(/(\d+)'\s*(\d+)"/);
        if (heightMatch) {
          const feet = parseInt(heightMatch[1]);
          const inches = parseInt(heightMatch[2]);
          heightInCm = Math.round((feet * 30.48) + (inches * 2.54));
        }
      }

      // Extract location - prioritize family location for city/state/country
      let city = '';
      let state = '';
      let country = 'India';
      
      // First try family location (most detailed)
      const familyLocation = profileData.flags?.family?.location || profileData.detailed?.family?.items?.find((item: any) => 
        item.iconClass === 'family_details_location')?.title;
      
      if (familyLocation) {
        const parts = familyLocation.split(',').map((s: string) => s.trim());
        if (parts.length >= 3) {
          city = parts[0];
          state = parts[1];
          country = parts[2];
        } else if (parts.length === 2) {
          state = parts[0];
          country = parts[1];
        } else if (parts.length === 1) {
          state = parts[0];
        }
      }
      
      // Fallback to base location info
      if (!state) {
        const locationInfo = profileData.base?.infoMap?.find((item: any) => item.label === 'Location');
        if (locationInfo) {
          const parts = locationInfo.value.split(',').map((s: string) => s.trim());
          if (parts.length === 1) {
            state = parts[0]; // Just state name
          } else if (parts.length >= 2) {
            city = parts[0];
            state = parts[1];
          }
        }
      }
      
      // Try to extract city from background if still missing
      if (!city) {
        const livingInfo = profileData.detailed?.background?.find((item: any) => 
          item.icon === 'profile_living_in');
        if (livingInfo?.desc) {
          const match = livingInfo.desc.match(/Lives in ([^,]+),\s*([^,]+),\s*(.+)/);
          if (match) {
            city = match[1];
            state = match[2];
            country = match[3];
          }
        }
      }

      // Extract profession and company
      const professionInfo = profileData.base?.infoMap?.find((item: any) => item.label === 'Profession');
      let profession = professionInfo?.value || '';
      let company = '';
      
      // Try to parse "Software Engineer at Google" format
      if (profession.includes(' at ')) {
        const parts = profession.split(' at ');
        profession = parts[0].trim();
        company = parts[1].trim();
      } else if (profession.includes(' in ')) {
        const parts = profession.split(' in ');
        profession = parts[0].trim();
        company = parts[1].trim();
      }
      
      // Fallback to detailed.profession fields
      if (!company) {
        company = profileData.detailed?.profession?.company_name || 
                  profileData.detailed?.profession?.company || 
                  profileData.detailed?.profession?.employer || '';
      }
      
      if (!profession) {
        profession = profileData.detailed?.profession?.designation || 
                     profileData.detailed?.profession?.occupation || '';
      }

      const educationInfo = profileData.detailed?.education?.items?.find((item: any) => 
        item.icon === 'edu_qualification') || profileData.detailed?.infoMapRevamp?.find((item: any) => 
        item.icon === 'edu_qualification' || item.icon === 'profile_education');
      // Parse education: "BBM - Bachelor of Business Management" or "Bachelors degree in Management"
      let educationRaw = educationInfo?.desc || educationInfo?.value || '';
      let educationField = '';
      
      if (educationRaw.includes(' - ')) {
        const parts = educationRaw.split(' - ');
        educationRaw = parts[1] || parts[0]; // Use full name
      } else if (educationRaw.includes(' in ')) {
        const match = educationRaw.match(/in (.+)$/);
        if (match) {
          educationField = match[1];
        }
      } else if (educationRaw.includes(' degree in ')) {
        const match = educationRaw.match(/degree in (.+)$/);
        if (match) {
          educationField = match[1];
        }
      }

      // Also check education.items for field
      const fieldInfo = profileData.detailed?.education?.items?.find((item: any) => item.icon === 'books');
      if (fieldInfo?.desc && !educationField) {
        educationField = fieldInfo.desc;
      }

      const religionInfo = profileData.base?.infoMap?.find((item: any) => item.label === 'Religion/Community');
      const religionParts = (religionInfo?.value || '').split(',').map((s: string) => s.trim());

      const motherTongueInfo = profileData.base?.infoMap?.find((item: any) => item.label === 'Mother Tongue');
      
      const maritalInfo = profileData.base?.infoMap?.find((item: any) => item.label === 'Marital Status');
      const maritalStatusRaw = maritalInfo?.value || profileData.marital_status || 'Never Married';

      const interests = (profileData.detailed?.personalityTags || []).map((tag: any) => tag.tag_display);

      // Parse income: "Earns INR 4 Lakh to 7 Lakh annually" or from income item
      const incomeInfo = profileData.detailed?.infoMapRevamp?.find((item: any) => 
        item.icon === 'profile_annualincome') || profileData.detailed?.education?.items?.find((item: any) => 
        item.icon === 'income');
      
      let incomeRaw = '';
      let familyIncome = '';
      
      if (incomeInfo?.desc) {
        // Parse: "<b>Self</b>: Earns INR 4 Lakh to 7 Lakh annually <br/> <b>Family</b>: Earns more than INR 70 lakhs annually"
        const selfMatch = incomeInfo.desc.match(/<b>Self<\/b>:\s*Earns\s*INR\s*([^<]+)/i);
        const familyMatch = incomeInfo.desc.match(/<b>Family<\/b>:\s*Earns\s*([^<]+)/i);
        
        if (selfMatch) {
          incomeRaw = selfMatch[1].replace(' annually', '').trim();
        }
        if (familyMatch) {
          familyIncome = familyMatch[1].replace(' annually', '').trim();
        }
      } else if (incomeInfo?.value) {
        incomeRaw = incomeInfo.value.replace('Earns ', '').replace(' annually', '');
      }

      const dietInfo = profileData.detailed?.lifestyle?.diet || 
        profileData.detailed?.infoMapRevamp?.find((item: any) => item.icon === 'profile_diet')?.value || '';
      const drinkInfo = profileData.detailed?.lifestyle?.drink || 
        profileData.detailed?.infoMapRevamp?.find((item: any) => item.icon === 'profile_drink')?.value || '';
      const smokeInfo = profileData.detailed?.lifestyle?.smoke || 
        profileData.detailed?.infoMapRevamp?.find((item: any) => item.icon === 'profile_smoke')?.value || '';

      const workingWithRaw = profileData.detailed?.profession?.working_with || '';

      // Parse partner preferences from Shaadi.com format
      let partnerPreferences: any = {};
      
      // Check for detailed.preferences.items (Shaadi.com format)
      if (profileData.detailed?.preferences?.items && Array.isArray(profileData.detailed.preferences.items)) {
        const items = profileData.detailed.preferences.items;
        
        // Parse Age Range: "29 to 34"
        const ageItem = items.find((item: any) => item.preferenceKey === 'age');
        if (ageItem?.desc) {
          const ageMatch = ageItem.desc.match(/(\d+)\s*to\s*(\d+)/);
          if (ageMatch) {
            partnerPreferences.ageRange = {
              min: parseInt(ageMatch[1]),
              max: parseInt(ageMatch[2])
            };
          }
        }
        
        // Parse Height Range: "5' 8"(172cm) to 6' 4"(193cm)"
        const heightItem = items.find((item: any) => item.preferenceKey === 'height');
        if (heightItem?.desc) {
          const heightMatch = heightItem.desc.match(/(\d+)'\s*(\d+)".*?(\d+)cm.*?(\d+)'\s*(\d+)".*?(\d+)cm/);
          if (heightMatch) {
            partnerPreferences.heightRange = {
              min: parseInt(heightMatch[3]), // 172 cm
              max: parseInt(heightMatch[6])  // 193 cm
            };
          }
        }
        
        // Parse Marital Status: "Never Married"
        const maritalItem = items.find((item: any) => item.preferenceKey === 'maritalstatus');
        if (maritalItem?.desc) {
          const statuses = maritalItem.desc.split(',').map((s: string) => normalizeMaritalStatus(s.trim()));
          partnerPreferences.maritalStatus = statuses;
        }
        
        // Parse Religion/Community: "Agarwal, Baniya, Jain"
        const casteItem = items.find((item: any) => item.preferenceKey === 'caste');
        if (casteItem?.desc) {
          const communities = casteItem.desc.split(',').map((s: string) => s.trim());
          partnerPreferences.community = communities;
        }
        
        // Parse Mother Tongue: "Hindi, Punjabi, English"
        const motherTongueItem = items.find((item: any) => item.preferenceKey === 'mothertongue');
        if (motherTongueItem?.desc) {
          const languages = motherTongueItem.desc.split(',').map((s: string) => s.trim());
          partnerPreferences.motherTongue = languages;
        }
        
        // Parse Diet: "Veg, Jain, Vegan"
        const dietItem = items.find((item: any) => item.preferenceKey === 'diet');
        if (dietItem?.desc) {
          const diets = dietItem.desc.split(',').map((s: string) => {
            const normalized = normalizeDiet(s.trim());
            return normalized || s.trim().toLowerCase();
          });
          partnerPreferences.diet = diets;
        }
      } 
      // Fallback to old format
      else if (profileData.preferences || profileData.partner_preferences || profileData.partnerExpectations) {
        const prefs = profileData.preferences || profileData.partner_preferences || profileData.partnerExpectations;
        
        partnerPreferences = {
          ageRange: {
            min: prefs.age_from || prefs.ageFrom || prefs.minAge || undefined,
            max: prefs.age_to || prefs.ageTo || prefs.maxAge || undefined
          },
          heightRange: {
            min: prefs.height_from || prefs.heightFrom || prefs.minHeight || undefined,
            max: prefs.height_to || prefs.heightTo || prefs.maxHeight || undefined
          },
          maritalStatus: Array.isArray(prefs.marital_status) ? prefs.marital_status : 
                         prefs.maritalStatus ? [prefs.maritalStatus] : [],
          religion: Array.isArray(prefs.religion) ? prefs.religion : 
                    prefs.religion ? [prefs.religion] : [],
          community: Array.isArray(prefs.community) ? prefs.community : 
                     prefs.community ? [prefs.community] : [],
          education: Array.isArray(prefs.education) ? prefs.education : 
                     prefs.education ? [prefs.education] : [],
          occupation: Array.isArray(prefs.occupation) ? prefs.occupation : 
                      prefs.occupation ? [prefs.occupation] : [],
          annualIncome: Array.isArray(prefs.annual_income) ? prefs.annual_income : 
                        prefs.annualIncome ? [prefs.annualIncome] : [],
          country: Array.isArray(prefs.country) ? prefs.country : 
                   prefs.country ? [prefs.country] : [],
          state: Array.isArray(prefs.state) ? prefs.state : 
                 prefs.state ? [prefs.state] : [],
          city: Array.isArray(prefs.city) ? prefs.city : 
                prefs.city ? [prefs.city] : [],
          diet: Array.isArray(prefs.diet) ? prefs.diet : 
                prefs.diet ? [prefs.diet] : [],
          smoking: Array.isArray(prefs.smoking) ? prefs.smoking : 
                   prefs.smoking ? [prefs.smoking] : [],
          drinking: Array.isArray(prefs.drinking) ? prefs.drinking : 
                    prefs.drinking ? [prefs.drinking] : [],
          motherTongue: Array.isArray(prefs.mother_tongue) ? prefs.mother_tongue : 
                        prefs.motherTongue ? [prefs.motherTongue] : []
        };
        
        // Remove ageRange if both min and max are undefined
        if (!partnerPreferences.ageRange.min && !partnerPreferences.ageRange.max) {
          delete partnerPreferences.ageRange;
        }
        
        // Remove heightRange if both min and max are undefined
        if (!partnerPreferences.heightRange.min && !partnerPreferences.heightRange.max) {
          delete partnerPreferences.heightRange;
        }
      }

      const parsed: ParsedProfile = {
        name: profileData.fullName || profileData.name || '',
        email: profileData.contact?.email?.replace(/X/g, '') || '',
        phone: profileData.contact?.contact_number?.replace(/X/g, '') || '',
        gender: normalizeGender(profileData.gender || ''),
        dateOfBirth: '',
        age: age,
        height: heightInCm,
        maritalStatus: normalizeMaritalStatus(maritalStatusRaw),
        religion: normalizeReligion(religionParts[0] || ''),
        community: religionParts[1] || '',
        motherTongue: motherTongueInfo?.value || '',
        city: city,
        state: state,
        country: country,
        education: normalizeEducation(educationRaw),
        educationField: educationField,
        profession: profession,
        company: company,
        workingWith: normalizeEmployment(workingWithRaw),
        annualIncome: normalizeIncome(incomeRaw),
        familyIncome: familyIncome,
        diet: normalizeDiet(dietInfo),
        drink: normalizeHabit(drinkInfo),
        smoke: normalizeHabit(smokeInfo),
        about: profileData.summary?.shortBio || profileData.detailed?.about || '',
        familyDetails: profileData.detailed?.family?.desc || '',
        fatherProfession: profileData.flags?.family?.father_profession || profileData.detailed?.family?.father_profession || '',
        motherProfession: profileData.flags?.family?.mother_profession || profileData.detailed?.family?.mother_profession || '',
        siblings: `${profileData.flags?.family?.brothers || profileData.detailed?.family?.brothers || 0} Brothers, ${profileData.flags?.family?.sisters || profileData.detailed?.family?.sisters || 0} Sisters`,
        interests: interests,
        images: uniqueImages,
        rawData: profileData,
        partnerPreferences: partnerPreferences
      };

      setEditableProfile(parsed);
      setIsEditing(false);
      toast.success(`Parsed! ${uniqueImages.length} images found`);
    } catch (error: any) {
      toast.error('Failed to parse JSON');
      setEditableProfile(null);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleImport = async () => {
    if (!editableProfile) {
      toast.error('No profile data');
      return;
    }

    // Validate required fields
    if (!editableProfile.state?.trim()) {
      toast.error('State is required');
      setIsEditing(true);
      return;
    }
    if (!editableProfile.city?.trim()) {
      toast.error('City is required');
      setIsEditing(true);
      return;
    }

    // Validate images
    if (!editableProfile.images || editableProfile.images.length === 0) {
      toast.error('At least one image is required');
      return;
    }

    console.log('📤 Importing profile with images:', editableProfile.images);

    try {
      setIsImporting(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_URL}/admin/import-profile`,
        { profileData: editableProfile },
        { 
          headers: { Authorization: `Bearer ${token}` },
          timeout: 120000 // 2 minute timeout for image processing
        }
      );

      if (response.data.success) {
        const result = response.data.data;
        console.log('✅ Import successful:', result);
        console.log('✅ S3 URLs returned:', result.photoUrls);
        
        let message = `Profile imported! ${result.photosUploaded} of ${result.photosAttempted} photos uploaded`;
        if (result.photoErrors && result.photoErrors.length > 0) {
          message += `\nErrors: ${result.photoErrors.join(', ')}`;
          toast.warning(message);
        } else {
          toast.success(message);
        }
        
        toast.info(`Password: ${result.generatedPassword}`, { duration: 10000 });
        setJsonInput('');
        setEditableProfile(null);
        fetchImportedProfiles();
      } else {
        toast.error(response.data.message);
      }
    } catch (error: any) {
      console.error('❌ Import failed:', error);
      toast.error(error.response?.data?.message || error.message || 'Import failed');
    } finally {
      setIsImporting(false);
    }
  };

  const updateField = (field: keyof ParsedProfile, value: any) => {
    if (!editableProfile) return;
    setEditableProfile({ ...editableProfile, [field]: value });
  };

  const removeImage = (index: number) => {
    if (!editableProfile) return;
    const newImages = editableProfile.images.filter((_, idx) => idx !== index);
    setEditableProfile({ ...editableProfile, images: newImages });
    toast.success('Image removed');
  };

  // Location helpers
  const countryOptions = countries.map(c => ({ value: c.name, label: c.name }));
  const getStatesForCountry = () => {
    const countryCode = countries.find(c => c.name === editableProfile?.country)?.code || 'IN';
    const stateList = states[countryCode] || [];
    return stateList.map((s: any) => ({ value: s.name, label: s.name }));
  };
  const getCitiesForState = () => {
    const stateName = editableProfile?.state;
    if (!stateName) return [];
    const cityList = cities[stateName] || [];
    return cityList.map((c: string) => ({ value: c, label: c }));
  };

  const getSelectedLabel = (value: string, options: {value: string, label: string}[]) => {
    return options.find(o => o.value === value)?.label || value;
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl">
      <h1 className="text-3xl font-bold mb-2">Profile Importer</h1>
      <p className="text-muted-foreground mb-6">Import profiles from JSON data with automatic image processing</p>

      <Tabs defaultValue="import" className="space-y-4">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="import">
            <Upload className="w-4 h-4 mr-2" />
            Import Profile
          </TabsTrigger>
          <TabsTrigger value="imported">
            <Users className="w-4 h-4 mr-2" />
            Imported Profiles ({importedProfiles.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="import" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>JSON Input</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <Textarea
                placeholder='Paste JSON data here...'
                value={jsonInput}
                onChange={(e) => setJsonInput(e.target.value)}
                className="min-h-[300px] font-mono text-sm"
              />
              <div className="flex gap-2">
                <Button onClick={parseProfileData} disabled={!jsonInput || isProcessing} className="flex-1">
                  {isProcessing ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</>) : 
                    (<><RefreshCw className="w-4 h-4 mr-2" />Parse Data</>)}
                </Button>
                <Button variant="outline" onClick={() => { setJsonInput(''); setEditableProfile(null); }}>
                  <X className="w-4 h-4 mr-2" />Clear
                </Button>
              </div>
            </CardContent>
          </Card>

          {editableProfile && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Profile Preview</CardTitle>
                    <CardDescription>Review and edit before importing</CardDescription>
                  </div>
                  <Button onClick={() => setIsEditing(!isEditing)} variant="outline">
                    {isEditing ? (<><Save className="w-4 h-4 mr-2" />Done</>) : (<><Edit2 className="w-4 h-4 mr-2" />Edit</>)}
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <ScrollArea className="h-[600px]">
                  <div className="space-y-6 pr-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div><Label>Name</Label>
                        {isEditing ? <Input value={editableProfile.name} onChange={(e) => updateField('name', e.target.value)}/> : 
                        <p className="text-sm mt-1">{editableProfile.name}</p>}</div>
                      <div><Label>Gender</Label>
                        {isEditing ? <Select value={editableProfile.gender} onValueChange={(v) => updateField('gender', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{GENDER_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select> : <p className="text-sm mt-1">{getSelectedLabel(editableProfile.gender, GENDER_OPTIONS)}</p>}</div>
                      <div><Label>Age</Label>
                        {isEditing ? <Input type="number" value={editableProfile.age} onChange={(e) => updateField('age', parseInt(e.target.value))}/> : 
                        <p className="text-sm mt-1">{editableProfile.age} yrs</p>}</div>
                      <div><Label>Height</Label>
                        {isEditing ? <Select value={editableProfile.height.toString()} onValueChange={(v) => updateField('height', parseInt(v))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{HEIGHT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value.toString()}>{o.label}</SelectItem>)}</SelectContent>
                        </Select> : <p className="text-sm mt-1">{editableProfile.height} cm</p>}</div>
                      <div><Label>Email</Label>
                        {isEditing ? <Input value={editableProfile.email} onChange={(e) => updateField('email', e.target.value)}/> : 
                        <p className="text-sm mt-1">{editableProfile.email || 'Auto-generated'}</p>}</div>
                      <div><Label>Phone</Label>
                        {isEditing ? <Input value={editableProfile.phone} onChange={(e) => updateField('phone', e.target.value)}/> : 
                        <p className="text-sm mt-1">{editableProfile.phone || 'Auto-generated'}</p>}</div>
                      <div><Label>Marital Status</Label>
                        {isEditing ? <Select value={editableProfile.maritalStatus} onValueChange={(v) => updateField('maritalStatus', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>{MARITAL_STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                        </Select> : <p className="text-sm mt-1">{getSelectedLabel(editableProfile.maritalStatus, MARITAL_STATUS_OPTIONS)}</p>}</div>
                    </div>

                    <div className="space-y-3">
                      <h3 className="font-semibold flex items-center gap-2"><MapPin className="w-4 h-4" />Location</h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div><Label>Country *</Label>
                          {isEditing ? <Combobox value={editableProfile.country} onValueChange={(v) => updateField('country', v)} 
                            options={countryOptions} placeholder="Select country" searchPlaceholder="Search..."/> : 
                          <p className="text-sm mt-1">{editableProfile.country}</p>}</div>
                        <div><Label>State *</Label>
                          {isEditing ? <Combobox value={editableProfile.state} onValueChange={(v) => updateField('state', v)} 
                            options={getStatesForCountry()} placeholder="Select state" searchPlaceholder="Search..."/> : 
                          <p className="text-sm mt-1">{editableProfile.state}</p>}</div>
                        <div><Label>City *</Label>
                          {isEditing ? <Combobox value={editableProfile.city} onValueChange={(v) => updateField('city', v)} 
                            options={getCitiesForState()} placeholder="Select city" searchPlaceholder="Search..."/> : 
                          <p className="text-sm mt-1">{editableProfile.city}</p>}</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h3 className="font-semibold flex items-center gap-2"><Briefcase className="w-4 h-4" />Professional</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label>Education</Label>
                          {isEditing ? <Select value={editableProfile.education} onValueChange={(v) => updateField('education', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{EDUCATION_LEVEL_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select> : <p className="text-sm mt-1">{getSelectedLabel(editableProfile.education, EDUCATION_LEVEL_OPTIONS)}</p>}</div>
                        <div><Label>Profession</Label>
                          {isEditing ? <Input value={editableProfile.profession} onChange={(e) => updateField('profession', e.target.value)}/> : 
                          <p className="text-sm mt-1">{editableProfile.profession}</p>}</div>
                        <div><Label>Company</Label>
                          {isEditing ? <Input value={editableProfile.company} onChange={(e) => updateField('company', e.target.value)}/> : 
                          <p className="text-sm mt-1">{editableProfile.company}</p>}</div>
                        <div><Label>Working With</Label>
                          {isEditing ? <Select value={editableProfile.workingWith} onValueChange={(v) => updateField('workingWith', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{EMPLOYMENT_TYPE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select> : <p className="text-sm mt-1">{getSelectedLabel(editableProfile.workingWith, EMPLOYMENT_TYPE_OPTIONS)}</p>}</div>
                        <div><Label>Annual Income</Label>
                          {isEditing ? <Select value={editableProfile.annualIncome} onValueChange={(v) => updateField('annualIncome', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{ANNUAL_INCOME_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select> : <p className="text-sm mt-1">{getSelectedLabel(editableProfile.annualIncome, ANNUAL_INCOME_OPTIONS)}</p>}</div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h3 className="font-semibold flex items-center gap-2"><Heart className="w-4 h-4" />Religious & Lifestyle</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label>Religion</Label>
                          {isEditing ? <Select value={editableProfile.religion} onValueChange={(v) => updateField('religion', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{RELIGION_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select> : <p className="text-sm mt-1">{getSelectedLabel(editableProfile.religion, RELIGION_OPTIONS)}</p>}</div>
                        <div><Label>Community</Label>
                          {isEditing ? <Input value={editableProfile.community} onChange={(e) => updateField('community', e.target.value)}/> : 
                          <p className="text-sm mt-1">{editableProfile.community}</p>}</div>
                        <div><Label>Mother Tongue</Label>
                          {isEditing ? <Select value={editableProfile.motherTongue} onValueChange={(v) => updateField('motherTongue', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{MOTHER_TONGUE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select> : <p className="text-sm mt-1">{editableProfile.motherTongue}</p>}</div>
                        <div><Label>Diet</Label>
                          {isEditing ? <Select value={editableProfile.diet} onValueChange={(v) => updateField('diet', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{DIET_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select> : <p className="text-sm mt-1">{getSelectedLabel(editableProfile.diet, DIET_OPTIONS)}</p>}</div>
                        <div><Label>Drinking</Label>
                          {isEditing ? <Select value={editableProfile.drink} onValueChange={(v) => updateField('drink', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{HABIT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select> : <p className="text-sm mt-1">{getSelectedLabel(editableProfile.drink, HABIT_OPTIONS)}</p>}</div>
                        <div><Label>Smoking</Label>
                          {isEditing ? <Select value={editableProfile.smoke} onValueChange={(v) => updateField('smoke', v)}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>{HABIT_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                          </Select> : <p className="text-sm mt-1">{getSelectedLabel(editableProfile.smoke, HABIT_OPTIONS)}</p>}</div>
                      </div>
                    </div>

                    {editableProfile.about && (
                      <div><Label>About</Label>
                        {isEditing ? <Textarea value={editableProfile.about} onChange={(e) => updateField('about', e.target.value)} rows={4}/> : 
                        <p className="text-sm text-muted-foreground">{editableProfile.about}</p>}</div>
                    )}

                    <div className="space-y-3">
                      <h3 className="font-semibold flex items-center gap-2"><User className="w-4 h-4" />Family Details</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div><Label>Father Profession</Label>
                          {isEditing ? <Input value={editableProfile.fatherProfession} onChange={(e) => updateField('fatherProfession', e.target.value)}/> : 
                          <p className="text-sm mt-1">{editableProfile.fatherProfession || 'Not specified'}</p>}</div>
                        <div><Label>Mother Profession</Label>
                          {isEditing ? <Input value={editableProfile.motherProfession} onChange={(e) => updateField('motherProfession', e.target.value)}/> : 
                          <p className="text-sm mt-1">{editableProfile.motherProfession || 'Not specified'}</p>}</div>
                        <div><Label>Siblings</Label>
                          <p className="text-sm mt-1">{editableProfile.siblings}</p></div>
                        {editableProfile.familyDetails && (
                          <div className="md:col-span-2"><Label>Family Description</Label>
                            <p className="text-sm mt-1 text-muted-foreground">{editableProfile.familyDetails}</p></div>
                        )}
                      </div>
                    </div>

                    {editableProfile.interests.length > 0 && (
                      <div><Label>Interests</Label>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {editableProfile.interests.map((interest, idx) => <Badge key={idx} variant="secondary">{interest}</Badge>)}
                        </div></div>
                    )}

                    <div className="space-y-3">
                      <h3 className="font-semibold flex items-center gap-2"><Image className="w-4 h-4" />Images ({editableProfile.images.length})</h3>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        {editableProfile.images.map((img, idx) => (
                          <div key={idx} className="relative aspect-square bg-gray-100 rounded overflow-hidden group">
                            <img src={img} alt={`${idx + 1}`} className="w-full h-full object-cover" />
                            {idx === 0 && <Badge className="absolute bottom-1 left-1 z-10" variant="secondary">Profile</Badge>}
                            <Button 
                              onClick={() => removeImage(idx)} 
                              className="absolute top-1 right-1 h-7 w-7 bg-red-500 hover:bg-red-600 text-white opacity-0 group-hover:opacity-100 transition-opacity" 
                              size="icon" 
                              variant="destructive"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <Button onClick={handleImport} disabled={isImporting} className="w-full" size="lg">
                      {isImporting ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Importing...</>) : 
                        (<><Download className="w-4 h-4 mr-2" />Import Profile ({editableProfile.images.length} images)</>)}
                    </Button>
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="imported" className="space-y-4">
          {selectedProfiles.size > 0 && (
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {selectedProfiles.size} profile{selectedProfiles.size !== 1 ? 's' : ''} selected
                  </p>
                  <div className="flex gap-2">
                    <Button onClick={handleBulkActivate} size="sm" variant="outline">
                      <Eye className="w-4 h-4 mr-2" />
                      Activate Selected
                    </Button>
                    <Button onClick={handleBulkDeactivate} size="sm" variant="outline">
                      <EyeOff className="w-4 h-4 mr-2" />
                      Deactivate Selected
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {loadingProfiles ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
          ) : importedProfiles.length > 0 ? (
            <>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Checkbox
                    id="select-all"
                    checked={selectedProfiles.size === importedProfiles.length}
                    onCheckedChange={toggleSelectAll}
                  />
                  <Label htmlFor="select-all" className="cursor-pointer">
                    Select All
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground">
                  Total: {importedProfiles.length} profiles
                </p>
              </div>
              
              <ClientPaginationWrapper
                items={importedProfiles}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
                renderItems={(paginatedProfiles) => (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {paginatedProfiles.map((profile: any) => {
                      const isActive = profile.isActive === undefined ? true : profile.isActive;
                      const isSelected = selectedProfiles.has(profile._id);
                      
                      return (
                        <Card key={profile._id} className={isSelected ? 'ring-2 ring-primary' : ''}>
                      <CardContent className="p-4">
                        <div className="flex gap-3">
                          <div className="flex items-start pt-1">
                            <Checkbox
                              id={`profile-${profile._id}`}
                              checked={isSelected}
                              onCheckedChange={() => toggleProfileSelection(profile._id)}
                            />
                          </div>
                          
                          {profile.photos && profile.photos.length > 0 && profile.photos[0].url && (
                            <div className="w-24 h-24 rounded overflow-hidden bg-gray-100 flex-shrink-0 relative">
                              <img 
                                src={profile.photos[0].url} 
                                alt={profile.name} 
                                className="w-full h-full object-cover" 
                              />
                              {!isActive && (
                                <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                                  <EyeOff className="w-6 h-6 text-white" />
                                </div>
                              )}
                            </div>
                          )}
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <h3 className="font-semibold truncate">{profile.name}</h3>
                                  {isActive ? (
                                    <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                                      <Eye className="w-3 h-3 mr-1" />
                                      Active
                                    </Badge>
                                  ) : (
                                    <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200">
                                      <EyeOff className="w-3 h-3 mr-1" />
                                      Hidden
                                    </Badge>
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground">{profile.email}</p>
                              </div>
                              <Button 
                                onClick={() => handleDeleteProfile(profile._id)} 
                                variant="ghost" 
                                size="sm"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            <div className="space-y-1 text-sm mb-3">
                              <p><span className="text-muted-foreground">Age:</span> {profile.age} yrs</p>
                              <p><span className="text-muted-foreground">Location:</span> {profile.city}, {profile.state}</p>
                              <p><span className="text-muted-foreground">Profession:</span> {profile.profession}</p>
                              <p><span className="text-muted-foreground">Gender:</span> {profile.gender}</p>
                            </div>

                            <div className="flex items-center justify-between pt-2 border-t">
                              <div className="flex items-center gap-2">
                                <Switch
                                  id={`status-${profile._id}`}
                                  checked={isActive}
                                  onCheckedChange={() => handleToggleProfileStatus(profile._id, isActive)}
                                  disabled={updatingStatus === profile._id}
                                />
                                <Label htmlFor={`status-${profile._id}`} className="text-sm cursor-pointer">
                                  {isActive ? 'Visible in Discovery' : 'Hidden from Discovery'}
                                </Label>
                              </div>
                            </div>

                            {profile.photos && profile.photos.length > 1 && (
                              <div className="flex gap-1 mt-2">
                                {profile.photos.slice(0, 4).map((photo: any, idx: number) => (
                                  <div key={idx} className="w-10 h-10 rounded bg-gray-100 overflow-hidden">
                                    <img src={photo.url} alt="" className="w-full h-full object-cover" />
                                  </div>
                                ))}
                                {profile.photos.length > 4 && (
                                  <div className="w-10 h-10 rounded bg-gray-200 flex items-center justify-center text-xs">
                                    +{profile.photos.length - 4}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                      );
                    })}
                  </div>
                )}
              />
            </>
          ) : (
            <Card>
              <CardContent className="p-12 text-center">
                <Users className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground">No imported profiles found</p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}